import React, { useRef, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import FormDropdown from '../../../components/common/FormDropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import {
  scorerDetailsSchema,
  type ScorerDetailsFormValues,
} from '../validation/scorerDetailsSchema';
import {
  MATCHES_FORMAT_OPTIONS,
  SCORING_METHOD_OPTIONS,
  SCORING_INTERFACE_OPTIONS,
  LANGUAGE_OPTIONS,
} from '../constants/scorerDetailsConstants';
import './ScorerDetailsStep.scss';

const emptyScorer: ScorerDetailsFormValues = {
  yearsOfExperience: '',
  matchesFormatWorkedOn: '',
  familiarScoringMethod: '',
  pricingPerMatch: '',
  scoringInterfacePreference: '',
  preferredCity: '',
  pricingPerDay: '',
  languageKnown: '',
};

interface ScorerDetailsStepProps {
  initialValues?: ScorerDetailsFormValues;
  onNext: (data: ScorerDetailsFormValues) => void;
  onCompleteLater: () => void | Promise<void>;
  partialSaving?: boolean;
}

function ScorerDetailsStep({
  initialValues,
  onNext,
  onCompleteLater,
  partialSaving = false,
}: ScorerDetailsStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [certificateFileName, setCertificateFileName] = useState('');
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<ScorerDetailsFormValues>({
    resolver: yupResolver(scorerDetailsSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues ?? emptyScorer,
  });

  useEffect(() => {
    reset(initialValues ?? emptyScorer);
  }, [initialValues, reset]);

  const handleCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCertificateFileName(file.name);
      setCertificateFile(file);
    }
  };

  const handleCertificateClick = () => {
    fileInputRef.current?.click();
  };

  const onSubmit = async (values: ScorerDetailsFormValues) => {
    setIsSubmitting(true);
    try {
      void certificateFile;
      onNext(values);
    } catch {
      toast.error('Failed to continue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="scorer-details-form">
      <div className="scorer-details-two-col">
        <div className="scorer-details-col">
          <div className="scorer-details-field">
            <label htmlFor="yearsOfExperience">
              Years of Experience <span className="req">*</span>
            </label>
            <input
              id="yearsOfExperience"
              type="text"
              placeholder="Experience"
              className={errors.yearsOfExperience ? 'error' : ''}
              {...register('yearsOfExperience')}
            />
            {errors.yearsOfExperience && (
              <span className="field-error">
                {errors.yearsOfExperience.message}
              </span>
            )}
          </div>
          <div className="scorer-details-field">
            <label htmlFor="matchesFormatWorkedOn">
              Matches Format Worked On <span className="req">*</span>
            </label>
            <Controller
              name="matchesFormatWorkedOn"
              control={control}
              render={({ field }) => (
                <FormDropdown
                  id="matchesFormatWorkedOn"
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={MATCHES_FORMAT_OPTIONS}
                  hasError={!!errors.matchesFormatWorkedOn}
                />
              )}
            />
            {errors.matchesFormatWorkedOn && (
              <span className="field-error">
                {errors.matchesFormatWorkedOn.message}
              </span>
            )}
          </div>
          <div className="scorer-details-field">
            <label htmlFor="familiarScoringMethod">
              Familiar Scoring Method <span className="req">*</span>
            </label>
            <Controller
              name="familiarScoringMethod"
              control={control}
              render={({ field }) => (
                <FormDropdown
                  id="familiarScoringMethod"
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={SCORING_METHOD_OPTIONS}
                  hasError={!!errors.familiarScoringMethod}
                />
              )}
            />
            {errors.familiarScoringMethod && (
              <span className="field-error">
                {errors.familiarScoringMethod.message}
              </span>
            )}
          </div>
          <div className="scorer-details-field">
            <label htmlFor="pricingPerMatch">
              Pricing (Per Match) <span className="req">*</span>
            </label>
            <div className={`scorer-input-with-prefix ${errors.pricingPerMatch ? 'error' : ''}`}>
              <span className="prefix">₹</span>
              <input
                id="pricingPerMatch"
                type="text"
                placeholder="0"
                {...register('pricingPerMatch')}
              />
            </div>
            {errors.pricingPerMatch && (
              <span className="field-error">
                {errors.pricingPerMatch.message}
              </span>
            )}
          </div>
          <div className="scorer-details-field">
            <label htmlFor="languageKnown">
              Language Known <span className="req">*</span>
            </label>
            <Controller
              name="languageKnown"
              control={control}
              render={({ field }) => (
                <FormDropdown
                  id="languageKnown"
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={LANGUAGE_OPTIONS}
                  hasError={!!errors.languageKnown}
                />
              )}
            />
            {errors.languageKnown && (
              <span className="field-error">
                {errors.languageKnown.message}
              </span>
            )}
          </div>
        </div>
        <div className="scorer-details-col">
          <div className="scorer-details-field">
            <label htmlFor="scoringInterfacePreference">
              Scoring Interface Preference <span className="req">*</span>
            </label>
            <Controller
              name="scoringInterfacePreference"
              control={control}
              render={({ field }) => (
                <FormDropdown
                  id="scoringInterfacePreference"
                  ref={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={SCORING_INTERFACE_OPTIONS}
                  hasError={!!errors.scoringInterfacePreference}
                />
              )}
            />
            {errors.scoringInterfacePreference && (
              <span className="field-error">
                {errors.scoringInterfacePreference.message}
              </span>
            )}
          </div>
          <div className="scorer-details-field">
            <label htmlFor="preferredCity">
              Preferred City <span className="req">*</span>
            </label>
            <input
              id="preferredCity"
              type="text"
              placeholder="Enter City Name"
              className={errors.preferredCity ? 'error' : ''}
              {...register('preferredCity')}
            />
            {errors.preferredCity && (
              <span className="field-error">
                {errors.preferredCity.message}
              </span>
            )}
          </div>
          <div className="scorer-details-field">
            <label>Certificate Upload</label>
            <div
              className="scorer-file-upload"
              onClick={handleCertificateClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                aria-label="Upload certificate file"
                onChange={handleCertificateChange}
              />
              <span className="file-value">
                {certificateFileName || 'Choose file'}
              </span>
              <span className="upload-btn">Upload</span>
            </div>
          </div>
          <div className="scorer-details-field">
            <label htmlFor="pricingPerDay">
              Pricing (Per Day) <span className="req">*</span>
            </label>
            <div className={`scorer-input-with-prefix ${errors.pricingPerDay ? 'error' : ''}`}>
              <span className="prefix">₹</span>
              <input
                id="pricingPerDay"
                type="text"
                placeholder="0"
                {...register('pricingPerDay')}
              />
            </div>
            {errors.pricingPerDay && (
              <span className="field-error">
                {errors.pricingPerDay.message}
              </span>
            )}
          </div>

        </div>
      </div>

      <div className="scorer-details-actions">
        <button
          type="button"
          className="btn-complete-later"
          onClick={() => void onCompleteLater()}
          disabled={partialSaving}
        >
          {partialSaving ? 'Saving…' : 'Complete Profile Later'}
        </button>
        <button type="submit" className="btn-next" disabled={isSubmitting || !isValid}>
          {isSubmitting ? 'Saving...' : 'Next'}
        </button>
      </div>
    </form>
  );
}

export default ScorerDetailsStep;
